import { useCallback, useMemo, useState } from "react";
import {
  Button,
  Division,
  EmptyState,
  Flex,
  Form,
  Heading,
  Icon,
  Icons,
  Modal,
  Pagination,
  Paragraph,
  Spin,
  Table,
  type TableColumnType,
  useToast,
} from "@ir/ui";
import {
  DeleteAllMasterDataDeductionTypesDTO,
  DeleteMasterDataDeductionTypesDTO,
} from "@src/domain/dto/masterData";
import {
  MasterDataDeductionType,
  type IMasterDataDeductionType,
  type MasterDataDeductionType as MasterDataDeductionTypeEntity,
} from "@src/domain/entities/masterData";
import { Permissions } from "@src/domain/permissions";
import {
  CreateMasterDataDeductionTypeUseCase,
  DeleteAllMasterDataDeductionTypesUseCase,
  DeleteMasterDataDeductionTypesUseCase,
  UpdateMasterDataDeductionTypeUseCase,
} from "@src/application/useCases/masterData";
import HasAnyPermission from "@src/presentation/components/permissions/HasAnyPermission";
import { useUserPermissions } from "@src/presentation/hooks/permissions";
import { useMasterDataDeductionTypes } from "@src/presentation/providers/MasterDataDeductionTypesDataProvider";
import { MasterDataAddEditModal } from "./MasterDataAddEditModal";
import {
  MasterDataDeductionTypeAddEditForm,
  type DeductionCategorySelectOption,
  type DeductionTypeAddEditFormValues,
} from "./MasterDataDeductionTypeAddEditForm";
import {
  MasterDataDeductionTypeDeleteConfirmModal,
  MasterDataDeductionTypeDeleteToolbar,
  type MasterDataDeductionTypeDeleteMode,
} from "./MasterDataDeductionTypeDeleteActions";
import { MasterDataEditActionCell } from "./MasterDataEditActionCell";
import { MasterDataHelpTextPreviewModal } from "./MasterDataHelpTextPreviewModal";
import { MasterDataSearchInput } from "./MasterDataSearchInput";
import {
  MASTER_DATA_COLUMN_VERTICAL_ALIGN,
  createMasterDataLinkedSelectionColumn,
  renderMasterDataHelpTextViewCell,
  renderMasterDataLinkedNameCell,
  renderMasterDataTextCell,
  sumMasterDataColumnWidths,
} from "./masterDataTable.shared";
import {
  applyDeductionTypeSaveError,
  getDeductionTypeFormInitialValues,
  getMasterDataDeleteErrorMessage,
  notifyDeductionTypeDeleteResult,
  saveMasterDataDeductionType,
} from "./deductionTypeForm.shared";
import { useMasterDataAddEditModal } from "./useMasterDataAddEditModal";
import { useMasterDataDeductionSubTypes } from "./useMasterDataDeductionSubTypes";

export const DEDUCTION_SUBTYPES_PAGE_SIZE = 100;

export interface MasterDataDeductionSubTypesModalProps {
  isOpen: boolean;
  parentTypeId: number | null;
  parentTypeName: string;
  categoryOptions: DeductionCategorySelectOption[];
  categoriesLoading?: boolean;
  onClose: () => void;
}

interface SubTypeRow extends IMasterDataDeductionType {
  key: string;
}

interface BuildSubTypeColumnsArgs {
  canDelete: boolean;
  selectedRowKeys: string[];
  selectableKeys: string[];
  onSelectionChange: (keys: Set<string>) => void;
  typesById: Map<number, MasterDataDeductionTypeEntity>;
  onEdit: (entity: MasterDataDeductionTypeEntity) => void;
  onViewHelpText: (text: string, name: string) => void;
}

function buildSubTypeColumns({
  canDelete,
  selectedRowKeys,
  selectableKeys,
  onSelectionChange,
  typesById,
  onEdit,
  onViewHelpText,
}: BuildSubTypeColumnsArgs): TableColumnType<SubTypeRow>[] {
  const dataColumns: TableColumnType<SubTypeRow>[] = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      align: "left",
      verticalAlign: MASTER_DATA_COLUMN_VERTICAL_ALIGN,
      width: 220,
      cellRender: (value: unknown, record: SubTypeRow) =>
        renderMasterDataLinkedNameCell(value, record),
    },
    {
      title: "Key",
      dataIndex: "deductionTypeKey",
      key: "deductionTypeKey",
      align: "left",
      verticalAlign: MASTER_DATA_COLUMN_VERTICAL_ALIGN,
      width: 180,
      cellRender: renderMasterDataTextCell,
    },
    {
      title: "Category",
      dataIndex: "categoryName",
      key: "categoryName",
      align: "left",
      verticalAlign: MASTER_DATA_COLUMN_VERTICAL_ALIGN,
      width: 160,
      cellRender: renderMasterDataTextCell,
    },
    {
      title: "Help Text",
      dataIndex: "helpText",
      key: "helpText",
      align: "left",
      verticalAlign: MASTER_DATA_COLUMN_VERTICAL_ALIGN,
      width: 110,
      cellRender: (value: unknown, record: SubTypeRow) =>
        renderMasterDataHelpTextViewCell(
          value,
          (text) => onViewHelpText(text, record.name),
          record.name,
        ),
    },
    {
      title: "Actions",
      dataIndex: "typeId",
      key: "actions",
      align: "center",
      verticalAlign: MASTER_DATA_COLUMN_VERTICAL_ALIGN,
      width: 80,
      cellRender: (value: unknown) => (
        <MasterDataEditActionCell
          entityId={value}
          entitiesById={typesById}
          onEdit={onEdit}
        />
      ),
    },
  ];
  if (!canDelete) return dataColumns;
  return [
    createMasterDataLinkedSelectionColumn<SubTypeRow>({
      selectedKeys: new Set(selectedRowKeys),
      selectableKeys,
      onChange: onSelectionChange,
      getAriaLabel: (record) =>
        record.isLinked
          ? `${record.name} is linked and cannot be selected`
          : `Select ${record.name}`,
    }),
    ...dataColumns,
  ];
}

function mapRows(types: MasterDataDeductionTypeEntity[]): SubTypeRow[] {
  return types.map((entity) => ({
    ...MasterDataDeductionType.toJSON(entity),
    key: String(entity.typeId),
  }));
}

export const MasterDataDeductionSubTypesModal = ({
  isOpen,
  parentTypeId,
  parentTypeName,
  categoryOptions,
  categoriesLoading = false,
  onClose,
}: MasterDataDeductionSubTypesModalProps) => {
  const { brandKey, refetch: refetchParent } = useMasterDataDeductionTypes();
  const { success: successToast, error: errorToast } = useToast();
  const { hasAnyPermission } = useUserPermissions();
  const [form] = Form.useForm<DeductionTypeAddEditFormValues>();
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [deleteMode, setDeleteMode] =
    useState<MasterDataDeductionTypeDeleteMode | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [helpTextPreview, setHelpTextPreview] = useState<{
    text: string;
    name: string;
  } | null>(null);

  const {
    page,
    setPage,
    pageSize,
    search,
    setSearch,
    items,
    itemTotal,
    loading,
    error,
    handleSaved,
    resetOnClose,
    repository,
  } = useMasterDataDeductionSubTypes({
    isOpen,
    parentTypeId,
    brandKey,
    refetchParent,
    pageSize: DEDUCTION_SUBTYPES_PAGE_SIZE,
  });

  const {
    modalMode,
    editingEntity,
    isSaving,
    setIsSaving,
    isFormSubmittable,
    setIsFormSubmittable,
    closeModal: closeFormState,
    openCreateModal,
    openEditModal,
  } = useMasterDataAddEditModal<MasterDataDeductionTypeEntity>();

  const createUseCase = useMemo(
    () => new CreateMasterDataDeductionTypeUseCase(repository),
    [repository],
  );
  const updateUseCase = useMemo(
    () => new UpdateMasterDataDeductionTypeUseCase(repository),
    [repository],
  );
  const deleteUseCase = useMemo(
    () => new DeleteMasterDataDeductionTypesUseCase(repository),
    [repository],
  );
  const deleteAllUseCase = useMemo(
    () => new DeleteAllMasterDataDeductionTypesUseCase(repository),
    [repository],
  );

  const canDelete = hasAnyPermission([Permissions.MASTER_DATA_DELETE]);
  const typesById = useMemo(() => {
    const map = new Map<number, MasterDataDeductionTypeEntity>();
    for (const entity of items) {
      map.set(entity.typeId, entity);
    }
    return map;
  }, [items]);

  const closeForm = useCallback(() => {
    closeFormState(form);
  }, [closeFormState, form]);

  const initialValues = useMemo(
    () => getDeductionTypeFormInitialValues(modalMode, editingEntity),
    [editingEntity, modalMode],
  );

  const handleSubmit = useCallback(
    async (values: DeductionTypeAddEditFormValues) => {
      if (parentTypeId == null) return;
      setIsSaving(true);
      try {
        await saveMasterDataDeductionType({
          values,
          modalMode,
          editingEntity,
          parentId: parentTypeId,
          createUseCase,
          updateUseCase,
        });

        successToast({ content: "Deduction sub-type saved successfully" });
        closeForm();
        handleSaved();
      } catch (err) {
        applyDeductionTypeSaveError(
          err,
          form,
          setIsFormSubmittable,
          errorToast,
          "Failed to save deduction sub-type. Please try again.",
          "Failed to save deduction sub-type. Please try again later.",
        );
      } finally {
        setIsSaving(false);
      }
    },
    [
      closeForm,
      createUseCase,
      editingEntity,
      errorToast,
      form,
      handleSaved,
      modalMode,
      parentTypeId,
      setIsFormSubmittable,
      setIsSaving,
      successToast,
      updateUseCase,
    ],
  );

  const handleConfirmDelete = useCallback(async () => {
    if (parentTypeId == null || deleteMode == null) return;
    setIsDeleting(true);
    try {
      const result =
        deleteMode === "all"
          ? await deleteAllUseCase.execute(
              new DeleteAllMasterDataDeductionTypesDTO({
                parentId: parentTypeId,
              }),
            )
          : await deleteUseCase.execute(
              new DeleteMasterDataDeductionTypesDTO({
                typeIds: Array.from(selectedIds),
              }),
            );

      notifyDeductionTypeDeleteResult(
        result,
        { singular: "sub-type", plural: "sub-types" },
        successToast,
        errorToast,
      );
      setSelectedIds(new Set());
      setDeleteMode(null);
      handleSaved();
    } catch (err) {
      errorToast({
        content: getMasterDataDeleteErrorMessage(
          err,
          "Failed to delete deduction sub-types. Please try again.",
        ),
      });
    } finally {
      setIsDeleting(false);
    }
  }, [
    deleteAllUseCase,
    deleteMode,
    deleteUseCase,
    errorToast,
    handleSaved,
    parentTypeId,
    selectedIds,
    successToast,
  ]);

  const selectedRowKeys = useMemo(
    () => Array.from(selectedIds).map(String),
    [selectedIds],
  );
  const selectableKeys = useMemo(
    () =>
      items
        .filter((entity) => !entity.isLinked)
        .map((entity) => String(entity.typeId)),
    [items],
  );

  const handleSelectionChange = useCallback(
    (keys: Set<string>) => {
      const next = new Set<number>();
      for (const key of keys) {
        const id = Number(key);
        const entity = typesById.get(id);
        if (entity && !entity.isLinked) next.add(id);
      }
      setSelectedIds(next);
    },
    [typesById],
  );

  const handleViewHelpText = useCallback((text: string, name: string) => {
    setHelpTextPreview({ text, name });
  }, []);

  const columns = useMemo(
    () =>
      buildSubTypeColumns({
        canDelete,
        selectedRowKeys,
        selectableKeys,
        onSelectionChange: handleSelectionChange,
        typesById,
        onEdit: openEditModal,
        onViewHelpText: handleViewHelpText,
      }),
    [
      canDelete,
      handleSelectionChange,
      handleViewHelpText,
      openEditModal,
      selectableKeys,
      selectedRowKeys,
      typesById,
    ],
  );

  const tableWidth = useMemo(
    () => sumMasterDataColumnWidths(columns),
    [columns],
  );

  const dataSource = useMemo(() => mapRows(items), [items]);

  const handleClose = useCallback(() => {
    closeForm();
    resetOnClose();
    setSelectedIds(new Set());
    setHelpTextPreview(null);
    onClose();
  }, [closeForm, onClose, resetOnClose]);

  const selectedCount = selectedIds.size;
  const showPagination = itemTotal > pageSize;

  let body = null;
  if (loading && dataSource.length === 0) {
    body = (
      <Flex align="center" justifyContent="center" className="py-6">
        <Spin tip="Loading..." spinning />
      </Flex>
    );
  } else if (error) {
    body = <Paragraph className="text-danger m-0">{error}</Paragraph>;
  } else if (dataSource.length === 0) {
    body = (
      <EmptyState
        className="bg-white border-1 border-color-grey-25 px-4 py-5 m-4"
        icon={Icons.InfoOctagon}
        title={
          <Heading level={6} className="text-grey-700 font-bold mt-3">
            No sub deduction types for this type.
          </Heading>
        }
      />
    );
  } else {
    body = (
      <Division
        style={{
          flex: 1,
          minHeight: 0,
          minWidth: 0,
          width: "100%",
          overflow: "hidden",
        }}
      >
        <Table<SubTypeRow>
          columns={columns}
          dataSource={dataSource}
          rowHoverable
          aria-label="Sub deduction types"
          sticky
          scroll={{ x: tableWidth, y: "100%" }}
        />
      </Division>
    );
  }

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={handleClose}
        size="large"
        isFullScreen
        closable
        classNames={{ body: "master-data-subtypes-modal-body" }}
        headerContent={
          <Flex
            align="center"
            justifyContent="space-between"
            gap={1}
            className="w-100 pb-2"
          >
            <Flex>
              <Heading level={6} className="text-grey-700 font-bold m-0">
                {parentTypeName
                  ? `Deduction Sub-Types — ${parentTypeName}`
                  : "Deduction Sub-Types"}
              </Heading>
            </Flex>

            <Flex gap={0.5} align="center">
              <Flex className="me-2">
                <MasterDataSearchInput
                  placeholder="Search deduction sub-types"
                  searchQuery={search}
                  onSearch={setSearch}
                  onClear={() => setSearch("")}
                  minLength={1}
                  fields={["Name", "Key"]}
                />
              </Flex>
              <MasterDataDeductionTypeDeleteToolbar
                selectedCount={selectedCount}
                onDeleteSelected={() => setDeleteMode("selected")}
                onDeleteAll={() => setDeleteMode("all")}
              />
              <HasAnyPermission
                requiredPermissions={[Permissions.MASTER_DATA_ADD]}
              >
                <Button
                  size="small"
                  variant="outline"
                  onPress={openCreateModal}
                  isDisabled={loading || modalMode != null}
                >
                  <Icon slot="start" name={Icons.Plus} />
                  Add Deduction Sub-Type
                </Button>
              </HasAnyPermission>
            </Flex>
          </Flex>
        }
        footerContent={
          <Flex
            align="center"
            justifyContent="center"
            className="w-100 m-0 py-3"
            style={{ position: "relative" }}
          >
            {showPagination ? (
              <Pagination
                totalItems={itemTotal}
                itemsPerPage={pageSize}
                currentPage={page}
                onPageChange={setPage}
                isDisabled={loading}
                aria-label="Sub deduction types pagination"
              />
            ) : null}
            <Flex>
              <Button
                size="small"
                color="grey"
                onPress={handleClose}
                style={{ position: "absolute", right: 0 }}
              >
                Close
              </Button>
            </Flex>
          </Flex>
        }
      >
        <Flex
          direction="column"
          align="stretch"
          wrap="no-wrap"
          grow
          gap={1}
          className="master-data-subtypes-modal px-2 pb-2"
        >
          {body}
        </Flex>
      </Modal>

      <MasterDataAddEditModal
        mode={modalMode}
        createTitle="Add Deduction Sub-Type"
        editTitle="Edit Deduction Sub-Type"
        isSaving={isSaving}
        isFormSubmittable={isFormSubmittable}
        form={form}
        onClose={closeForm}
      >
        <MasterDataDeductionTypeAddEditForm
          form={form}
          mode={modalMode ?? "create"}
          initialValues={initialValues}
          categoryOptions={categoryOptions}
          categoriesLoading={categoriesLoading}
          showHelpText
          onSubmit={handleSubmit}
          onFormValidityChange={setIsFormSubmittable}
        />
      </MasterDataAddEditModal>

      <MasterDataHelpTextPreviewModal
        isOpen={helpTextPreview != null}
        title={
          helpTextPreview
            ? `Help Text — ${helpTextPreview.name}`
            : "Help Text"
        }
        content={helpTextPreview?.text ?? ""}
        onClose={() => setHelpTextPreview(null)}
      />

      <MasterDataDeductionTypeDeleteConfirmModal
        deleteMode={deleteMode}
        isDeleting={isDeleting}
        selectedCount={selectedCount}
        allTitle="Delete all sub-types"
        selectedTitle="Delete selected sub-types"
        allDescription="This will delete every unlinked sub-type for this deduction type, including other pages. Linked sub-types will remain."
        entitySingular="sub-type"
        entityPlural="sub-types"
        onClose={() => setDeleteMode(null)}
        onConfirm={handleConfirmDelete}
      />
    </>
  );
};
